import { useState, useEffect } from 'react';
import { X, FileText, Copy } from 'lucide-react';
import { MODEL_TYPES } from '../../utils/constants';
import useFlowStore from '../../stores/useFlowStore';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from './ConfirmDialog';
import { modelApi } from '../../utils/api';
import toast from 'react-hot-toast';

const NewModelModal = ({ onClose }) => {
  const { 
    resetFlow, 
    setModelName, 
    setModelType, 
    setModelDescription, 
    setNodes,
    setEdges,
    setGlobalTags,
    isDirty, 
    modelName, 
    nodes 
  } = useFlowStore();
  
  const [creationType, setCreationType] = useState('blank'); // 'blank' or 'copy'
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedModelData, setSelectedModelData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    type: MODEL_TYPES.RELATIONAL,
    description: '',
  });
  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel } = useConfirm();

  // Load available models when modal opens
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await modelApi.getAll();
        setAvailableModels(response.data || []);
      } catch (error) {
        console.error('Error loading models:', error);
        toast.error('Failed to load models');
      }
    };
    loadModels();
  }, []);

  // Load selected model details
  useEffect(() => {
    const loadModelData = async () => {
      if (creationType === 'copy' && selectedModelId) {
        setLoading(true);
        try {
          const response = await modelApi.getById(selectedModelId);
          setSelectedModelData(response.data);
          // Auto-fill type and description from selected model
          setFormData(prev => ({
            ...prev,
            type: response.data.type || MODEL_TYPES.RELATIONAL,
            description: response.data.description || ''
          }));
        } catch (error) {
          console.error('Error loading model:', error);
          toast.error('Failed to load model details');
          setSelectedModelData(null);
        } finally {
          setLoading(false);
        }
      } else {
        setSelectedModelData(null);
      }
    };
    loadModelData();
  }, [selectedModelId, creationType]);

  const createNewModel = () => {
    try {
      if (creationType === 'blank') {
        // Create blank model
        resetFlow();
        setModelName(formData.name);
        setModelType(formData.type);
        setModelDescription(formData.description);
        onClose();
      } else {
        // Create model based on existing one
        if (!selectedModelData) {
          toast.error('Model data not loaded. Please try again.');
          onClose();
          return;
        }
        
        resetFlow();
        setModelName(formData.name);
        setModelType(selectedModelData.type || MODEL_TYPES.RELATIONAL);
        setModelDescription(formData.description);
        
        // Use setTimeout to ensure resetFlow completes before copying data
        setTimeout(() => {
          setNodes(selectedModelData.nodes || []);
          setEdges(selectedModelData.edges || []);
          setGlobalTags(selectedModelData.globalTags || []);
        }, 100);
        
        onClose();
      }
    } catch (error) {
      console.error('Error creating model:', error);
      toast.error('Failed to create model');
      onClose(); // Close anyway to prevent stuck modal
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a model name');
      return;
    }

    if (creationType === 'copy' && !selectedModelId) {
      toast.error('Please select a model to copy from');
      return;
    }

    if (creationType === 'copy' && !selectedModelData) {
      toast.error('Model data is still loading. Please wait...');
      return;
    }

    // Check if there are unsaved changes
    if (isDirty && modelName && nodes.length > 0) {
      confirm({
        title: 'Unsaved Changes',
        message: `You have unsaved changes in "${modelName}". Creating a new model will discard these changes. Do you want to continue?`,
        confirmText: 'Discard & Create New',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: createNewModel
      });
    } else {
      createNewModel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            New Model
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Creation Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Create From *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setCreationType('blank');
                  setSelectedModelId('');
                  setFormData(prev => ({ ...prev, type: MODEL_TYPES.RELATIONAL, description: '' }));
                }}
                className={`flex items-center justify-center gap-2 p-3 border-2 rounded-lg transition-all ${
                  creationType === 'blank'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <FileText size={20} />
                <span className="font-medium">New Model</span>
              </button>
              
              <button
                type="button"
                onClick={() => setCreationType('copy')}
                className={`flex items-center justify-center gap-2 p-3 border-2 rounded-lg transition-all ${
                  creationType === 'copy'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <Copy size={20} />
                <span className="font-medium">Copy Existing</span>
              </button>
            </div>
          </div>

          {/* Model Selection (only when copying) */}
          {creationType === 'copy' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Model to Copy *
              </label>
              <select
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required={creationType === 'copy'}
              >
                <option value="">-- Select a model --</option>
                {availableModels.map(model => (
                  <option key={model._id} value={model._id}>
                    {model.name} ({model.type})
                  </option>
                ))}
              </select>
              {selectedModelId && selectedModelData && (
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Will copy {selectedModelData.nodes?.length || 0} entities and {selectedModelData.edges?.length || 0} relationships
                </p>
              )}
            </div>
          )}

          {/* Model Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Model Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="e.g., E-commerce Database v2"
              required
              autoFocus
            />
          </div>

          {/* Model Type (only for blank models) */}
          {creationType === 'blank' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Model Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={MODEL_TYPES.RELATIONAL}>Relational (SQL)</option>
                <option value={MODEL_TYPES.NOSQL} disabled>NoSQL (Document) - Coming Soon</option>
                <option value={MODEL_TYPES.ANALYTICAL}>Analytical (Star Schema)</option>
              </select>
            </div>
          )}

          {/* Type info when copying */}
          {creationType === 'copy' && selectedModelData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Model Type
              </label>
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300">
                {selectedModelData.type === MODEL_TYPES.RELATIONAL && 'Relational (SQL)'}
                {selectedModelData.type === MODEL_TYPES.NOSQL && 'NoSQL (Document)'}
                {selectedModelData.type === MODEL_TYPES.ANALYTICAL && 'Analytical (Star Schema)'}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Type will be copied from the selected model
              </p>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              placeholder={creationType === 'copy' ? "Optional: customize description..." : "Optional description..."}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Create Model'}
            </button>
          </div>
        </form>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
        onCancel={cancel}
      />
    </div>
  );
};

export default NewModelModal;
